import { useMemo, useState } from "react";
import { Download, Megaphone, Pencil, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useActualizeazaTargetareTinta } from "@/hooks/api/mutate/useActualizeazaTargetareTinta";
import { useStergeTargetareCampanie } from "@/hooks/api/mutate/useStergeTargetareCampanie";
import { useStergeTargetareTinta } from "@/hooks/api/mutate/useStergeTargetareTinta";
import { useGetTargetareTinte } from "@/hooks/api/query/useGetTargetareTinte";
import { useToast } from "@/hooks/use-toast";
import type { TargetareCampanie, TargetareTinta } from "@/types/targetare";
import { descarcaFisierText, formatNumar } from "@/utils/targetareFormatting";
import {
  definitieRaportTinte,
  randuriTinte,
} from "@/utils/targetareRaportTinte";
import {
  ETICHETE_CANAL,
  ETICHETE_STARE_CAMPANIE,
  csvGoogleAds,
  csvMailchimp,
} from "@/utils/targetareTinte";
import { FisaFirmaDialog } from "../components/FisaFirmaDialog";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";
import { AdaugaTinteDialog } from "../components/tinte/AdaugaTinteDialog";
import { CampanieDialog } from "../components/tinte/CampanieDialog";
import { celulaTinta } from "../components/tinte/CelulaTinta";

export default function TinteTargetare() {
  const { toast } = useToast();
  const tinte = useGetTargetareTinte();
  const actualizeaza = useActualizeazaTargetareTinta();
  const sterge = useStergeTargetareTinta();
  const stergeCampanie = useStergeTargetareCampanie();
  const [dialogAdauga, setDialogAdauga] = useState(false);
  const [dialogCampanie, setDialogCampanie] = useState<{
    deschis: boolean;
    campanie: TargetareCampanie | null;
  }>({ deschis: false, campanie: null });
  const [cuiSelectat, setCuiSelectat] = useState<string | null>(null);

  const date = tinte.data;
  const campanii = useMemo(() => date?.campanii ?? [], [date]);
  const randuri = useMemo(
    () => randuriTinte(date?.tinte ?? [], campanii),
    [date, campanii]
  );

  const eroare = (titlu: string) => (e: Error) =>
    toast({ title: titlu, description: e.message, variant: "destructive" });

  const exporta = (
    format: "mailchimp" | "google_ads",
    lista: TargetareTinta[]
  ) => {
    const zi = new Date().toISOString().slice(0, 10);
    if (format === "mailchimp") {
      descarcaFisierText(
        csvMailchimp(lista, campanii),
        `tinte-mailchimp-${zi}.csv`
      );
    } else {
      descarcaFisierText(csvGoogleAds(lista), `tinte-google-ads-${zi}.csv`);
    }
  };
  const selectie = (ids: string[]) =>
    randuri.filter((r) => ids.includes(r.cheie));

  const celula = useMemo(
    () =>
      celulaTinta({
        campanii,
        onActualizeaza: (patch) =>
          actualizeaza.mutate(patch, {
            onError: eroare("Actualizarea a eșuat"),
          }),
        onFisa: setCuiSelectat,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campanii]
  );

  const stats = date?.stats;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-xl font-heading font-semibold">
            Evidența firmelor de targetat
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Firmele alese pentru ofertare, pe sectoare și campanii, cu starea
            discuției, responsabil, următoarea acțiune și note, editabile direct
            în tabel. Exporturile sunt în formatul audiențelor Mailchimp și al
            listelor Customer Match din Google Ads.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setDialogCampanie({ deschis: true, campanie: null })}
          >
            <Megaphone className="mr-2 h-4 w-4" />
            Campanie nouă
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={randuri.length === 0}>
                <Download className="mr-2 h-4 w-4" />
                Export toate ({formatNumar(randuri.length)})
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exporta("mailchimp", randuri)}>
                CSV audiență Mailchimp (doar cu email)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exporta("google_ads", randuri)}>
                CSV Customer Match Google Ads
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setDialogAdauga(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Adaugă ținte
          </Button>
        </div>
      </div>

      {tinte.isLoading ? (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : (
        stats && (
          <div className="grid gap-3 grid-cols-2 md:grid-cols-6">
            {[
              ["Total", stats.total],
              ["De contactat", stats.pe_stare.de_contactat ?? 0],
              [
                "În lucru",
                (stats.pe_stare.contactat ?? 0) +
                  (stats.pe_stare.oferta_trimisa ?? 0) +
                  (stats.pe_stare.in_discutie ?? 0),
              ],
              ["Câștigate", stats.pe_stare.castigat ?? 0],
              ["Acțiuni scadente", stats.de_urmarit],
              ["Cu email", stats.cu_email],
            ].map(([eticheta, valoare]) => (
              <Card key={String(eticheta)}>
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    {eticheta}
                  </p>
                  <p className="text-2xl font-heading font-semibold tabular-nums">
                    {formatNumar(Number(valoare))}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {campanii.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {campanii.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
            >
              <span className="font-medium">{c.nume}</span>
              <Badge variant="outline" className="font-normal">
                {ETICHETE_CANAL[c.canal]}
              </Badge>
              <Badge variant="secondary" className="font-normal">
                {ETICHETE_STARE_CAMPANIE[c.stare]}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {formatNumar(c.nr_tinte)} firme
                {c.sector && ` · ${c.sector}`}
              </span>
              <button
                type="button"
                className="rounded p-1 text-muted-foreground hover:bg-muted"
                title="Editează"
                onClick={() =>
                  setDialogCampanie({ deschis: true, campanie: c })
                }
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                title="Șterge campania (țintele rămân, fără campanie)"
                onClick={() =>
                  stergeCampanie.mutate(
                    { id: c.id },
                    { onError: eroare("Ștergerea a eșuat") }
                  )
                }
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {tinte.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Evidența nu a putut fi încărcată: {tinte.error.message}
        </p>
      )}

      <RaportInteractiv
        definitie={definitieRaportTinte}
        randuri={randuri}
        isLoading={tinte.isLoading}
        celula={celula}
        meniuRand={(r) => (
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
            title="Scoate din evidență"
            onClick={() =>
              sterge.mutate(
                { id: r.id },
                { onError: eroare("Ștergerea a eșuat") }
              )
            }
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        actiuniSelectie={[
          {
            eticheta: "Export Mailchimp (selecția)",
            onClick: (ids) => exporta("mailchimp", selectie(ids)),
          },
          {
            eticheta: "Export Google Ads (selecția)",
            onClick: (ids) => exporta("google_ads", selectie(ids)),
          },
          {
            eticheta: "Scoate selecția din evidență",
            onClick: (ids) =>
              selectie(ids).forEach((r) =>
                sterge.mutate(
                  { id: r.id },
                  { onError: eroare("Ștergerea a eșuat") }
                )
              ),
          },
        ]}
      />

      <AdaugaTinteDialog
        deschis={dialogAdauga}
        onClose={() => setDialogAdauga(false)}
        campanii={campanii}
      />
      <CampanieDialog
        deschis={dialogCampanie.deschis}
        campanie={dialogCampanie.campanie}
        onClose={() => setDialogCampanie({ deschis: false, campanie: null })}
      />
      <FisaFirmaDialog cui={cuiSelectat} onClose={() => setCuiSelectat(null)} />
    </div>
  );
}
